import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FilterItem} from '../../_models/FilterItem';
import {TipoRisorsa} from '../../_models/generated/tipo-risorsa';
import {TipoFormControl} from '../../_helpers/TipoFormControl';
import {TipoOperazione} from '../../_helpers/TipoOperazione';
import {SelectDateRangeComponent} from '../select/select-date-range/select-date-range.component';
import {ModalController} from '@ionic/angular';
import {STATO_ENUM_UI} from "../../_helpers/StatoEnum";
import {formatLabel} from "../../_helpers/label-utils";

@Component({
  selector: 'app-ordini-filter-row',
  templateUrl: './ordini-filter-row.component.html',
  styleUrls: ['./ordini-filter-row.component.scss'],
  standalone: false
})
export class OrdiniFilterRowComponent implements OnInit, OnChanges {
  // Breakpoint Tailwind `md`: sotto i 768px si usa il pannello filtri richiudibile.
  private static readonly MOBILE_BREAKPOINT = 768;
  private static readonly QUANTITA_SENTINEL = '-99999';
  // Etichette del pannello mobile per fieldName. `FilterItem.label` ha la precedenza,
  // il fallback finale è il fieldName stesso (così restano leggibili anche altri usi del filtro).
  private static readonly FIELD_LABELS: Record<string, string> = {
    NumRegistrazione: 'Registrazione',
    Commessa: 'Commessa',
    RifOrdine: 'Cod. Barre',
    CodiceOrdine: 'Ord.Riga.Fase',
    RifOdP: 'Rif. OdP',
    NumFase: 'Num. fase',
    DataRegistrazione: 'Creato',
    DataTermine: 'Consegna',
    Risorsa: 'Risorsa',
    RisorsaId: 'Risorsa',
    Lavorazione: 'Lavoro',
    LavorazioneId: 'Lavoro',
    CodiceArticolo: 'Articolo',
    VarianteArt: 'Variante',
    CodCliente: 'Cliente',
    IdOrdVen: 'OdV',
    CodTerzista: 'Terzista',
    UdM: 'Unità misura',
    QtaOrdine: 'Quantità',
    QuantitaLavorata: 'Quantità',
    Quantita: 'Residua',
    Stato: 'Stato',
    FaseContoLavoro: 'Conto lavoro'
  };

  @Input() filters: FilterItem[];
  @Input() desktopGridTemplateColumns?: string;
  @Output() filtersChange = new EventEmitter<FilterItem[]>();
  @Output() handleRefresh = new EventEmitter<number>();
  tipoFormControl = TipoFormControl;
  tipoOperazione = TipoOperazione;
  TipoRisorsa = TipoRisorsa;
  isMobile = false;
  panelOpen = false;
  // Snapshot dei valori di default per il tasto "Azzera" (i default variano per tipo di filtro).
  private readonly filterDefaults = new Map<FilterItem, { value1: string; value2: string }>();

  constructor(private modalController: ModalController) {
    this.updateIsMobile();
  }

  get activeCount(): number {
    return this.filters ? this.findVisible().filter(c => this.isFilterActive(c)).length : 0;
  }

  @HostListener('window:resize')
  onResize() {
    this.updateIsMobile();
  }

  ngOnInit() {
    this.loadStateOptions();
    this.snapshotDefaults();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.loadStateOptions();
      this.snapshotDefaults();
    }
  }

  refreshOrdine = () => {
    this.refresh();
  };

  findVisible(): any[] {
    return this.filters.filter(p => p.showColumn);
  }

  /*
   * Il backend deserializza FilterItem.value1 come stringa, mentre le option dei select possono esporre valori
   * numerici. Qui manteniamo sempre value1 come stringa per il payload API e convertiamo solo nel binding dei
   * select Risorsa e Lavorazione quando serve il numero, cosi la label selezionata resta visibile al primo click.
   */
  onColumnSelectValueChange(column: FilterItem, value: any) {
    column.value1 = value !== undefined && value !== null ? value.toString() : '';
    this.refresh();
  }

  toSelectNumericValue(value: any) {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    const numeric = +value;
    return Number.isNaN(numeric) ? value : numeric;
  }

  refresh = () => this.handleRefresh.emit(1);

  // Etichetta del campo nel pannello mobile.
  labelFor(column: FilterItem): string {
    return column.label ?? OrdiniFilterRowComponent.FIELD_LABELS[column.fieldName] ?? column.fieldName;
  }

  // Un filtro è "attivo" quando l'utente lo ha effettivamente ristretto rispetto al default.
  isFilterActive(column: FilterItem): boolean {
    const v1 = column.value1;
    if (v1 === undefined || v1 === null || v1 === '') {
      return false;
    }
    if (v1 === OrdiniFilterRowComponent.QUANTITA_SENTINEL) {
      return false;
    }
    if (column.tipoFormControl === TipoFormControl.datetime) {
      const startYear = new Date(v1).getFullYear();
      const endYear = column.value2 ? new Date(column.value2).getFullYear() : 9999;
      // I default coprono l'intero range 2000..2099: conta solo se ristretto.
      return startYear > 2000 || endYear < 2099;
    }
    return true;
  }

  // I campi che occupano più spazio (date, select, ordine, range) vanno a tutta larghezza nel grid mobile.
  isWideField(column: FilterItem): boolean {
    return column.tipoFormControl === TipoFormControl.datetime ||
      column.tipoFormControl === TipoFormControl.select ||
      column.tipoFormControl === TipoFormControl.selectOrdine ||
      column.operazione === TipoOperazione.between;
  }

  togglePanel() {
    this.panelOpen = !this.panelOpen;
  }

  applyAndClose() {
    this.panelOpen = false;
    this.refresh();
  }

  resetFilters() {
    this.filters?.forEach(f => {
      const def = this.filterDefaults.get(f);
      if (def) {
        f.value1 = def.value1;
        f.value2 = def.value2;
      }
    });
    this.refresh();
  }

  async openDateModal(column: FilterItem) {
    const modal = await this.modalController.create({
      component: SelectDateRangeComponent,
      componentProps: {
        singleDate: column.operazione !== this.tipoOperazione.between,
        startDate: column.value1, endDate: column.value2
      }
    });
    await modal.present();
    const {data, role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      column.value1 = data.startDate;
      column.value2 = data.endDate;
      this.refresh();
    }
  }

  private updateIsMobile() {
    this.isMobile = typeof window !== 'undefined' &&
      window.innerWidth < OrdiniFilterRowComponent.MOBILE_BREAKPOINT;
  }

  private snapshotDefaults() {
    if (!this.filters) {
      return;
    }
    this.filters.forEach(f => {
      if (!this.filterDefaults.has(f)) {
        this.filterDefaults.set(f, {value1: f.value1, value2: f.value2});
      }
    });
  }

  private loadStateOptions() {
    if (!this.filters) return;

    const filtersToLoad = this.filters.filter(f =>
      (f.fieldName === 'Stato' || f.fieldName === 'StatoRisorsa' || f.fieldName === 'StatoOrdine') &&
      f.tipoFormControl === TipoFormControl.select &&
      (!f.options || f.options.length === 0)
    );

    if (filtersToLoad.length === 0) return;

    const mappedOptions = Object.entries(STATO_ENUM_UI)
      .map(([k, ui]) => ({
        n: Number(k),
        value: String(k),
        text: formatLabel(ui.label)
      }))
      .sort((a, b) => a.n - b.n)
      .map(({n: _n, ...rest}) => rest);

    filtersToLoad.forEach(f => (f.options = mappedOptions));
  }
}
